import { Box, Hidden, IconButton, Theme, Typography, makeStyles } from '@material-ui/core';
import { ICommunity, ICourse, enLayout } from 'interface/course';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import HelpIcon from '@eduzz/houston-icons/HelpOutline';
import { IStore } from 'interface/redux';
import MenuIcon from '@material-ui/icons/Menu';
import News from './News';
import NotificationCenter from './NotificationCenter';
import Progress from 'components/Shared/Progress';
import SearchIcon from './Items/Search';
import SearchInputContainer from './Items/Search/FieldContainer';
import { SvgIcon } from 'components/Shared/SvgIcon';
import TurnProducerDialog from './TurnProducerDialog';
import UserMenu from './UserMenu';
import courseService from 'services/course';
import { fetchUserProfile } from 'actionCreators/user';
import { getStore } from 'redux/store';
import { isSubdomain } from 'utils/domain';
import { toggleShowMenu } from 'redux/lesson/slice';
import { useAppSelector } from 'hooks/redux';
import { useCustomization } from 'hooks/customization';

interface IProps {
  documentTitle?: string;
}

interface IStyleProps {
  color: string;
  background: string;
  logo: string;
}

const useStyles = makeStyles<Theme, IStyleProps>((theme) => ({
  headerContainer: {
    position: 'fixed',
    width: '100%',
    height: 56,
    top: 0,
    zIndex: 70,
  },
  header: {
    '@media(max-width: 425px)': {
      padding: theme.spacing(0, 2),
    },
    padding: theme.spacing(0, 4),
    height: 56,
    width: '100%',
    background: (props) => props.background ?? theme.palette.neutrals.black,
    display: 'flex',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& svg, .houston-icon, p, #news-icon': {
      color: (props) => props.color ?? theme.palette.common.white,
      fill: (props: IStyleProps) => props.color ?? theme.palette.common.white,
    },
  },
  headerActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  button: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.common.white,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  hideNutrorIconOnMobile: {
    maxHeight: 40,
    maxWidth: 120,
    height: 40,
    width: 120,
    display: 'flex',
    alignItems: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center left',
    backgroundSize: 'cover',
    backgroundImage: (props) => props.logo && `url(${props.logo})`,
    '& svg': {
      display: 'none',
      '&:first-child': {
        '@media(min-width: 426px)': {
          display: 'block',
        },
      },
      '&:last-child': {
        '@media(max-width: 425px)': {
          display: 'block',
        },
      },
    },
  },
  helpLink: {
    display: 'flex',
  },
  menuGroup: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const Header = (props: Partial<IProps>) => {
  const [modalIsOpen, setModalisOpen] = useState(false);
  const [communityUrl, setCommunityUrl] = useState<ICommunity>({} as ICommunity);
  const { courseHash } = useParams<{ courseHash: string }>();
  const history = useHistory();
  const isAuthenticated = useSelector((store: IStore) => store.auth.isAuthenticated);
  const user = useSelector((store: IStore) => store.user);
  const course = useAppSelector((store) => store.course as ICourse);
  const enableLessonMenu = useAppSelector((store) => store.newLesson.enableLessonMenu);
  const dispatch = useDispatch();
  const customization = useCustomization();
  const classes = useStyles({
    color: customization.header_link_color,
    background: customization.header_background_color,
    logo: customization.header_logo,
  });

  const toggleModalTurnProducer = () => {
    setModalisOpen((isOpen) => !isOpen);
  };

  const hrefToSupportPage = () => {
    if (customization.support_external_url) {
      return (
        <a
          id='header-external-support-page'
          href={customization.support_external_url}
          target='_blank'
          rel='noopener noreferrer'
          className={classes.helpLink}
        >
          <HelpIcon size={24} />
        </a>
      );
    }
    return (
      <Link id='header-support-page' className={classes.helpLink} to='/ajuda'>
        <HelpIcon size={24} />
      </Link>
    );
  };

  const openCertificatesPage = () => {
    history.push('/certificados');
  };

  const openSavedLessonsPage = () => {
    history.push('/aulas/salvas');
  };

  const toggleLessonMenu = () => {
    dispatch(toggleShowMenu());
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const store = getStore();
    fetchUserProfile()(store.dispatch, store.getState);
  }, [isAuthenticated]);

  useEffect(() => {
    document.title = props.documentTitle ? `Nutror - ${props.documentTitle}` : 'Nutror';
  }, [props]);

  const hrefToSecretaryPage = (): void => {
    window.open(course.secretary_url);
  };

  const hrefToCommunityPage = (): void => {
    courseService.getCommunityUrl(course.hash).then((data) => setCommunityUrl(data));
    window.open(communityUrl.url);
  };

  const hrefToDepoimentusPage = (): void => {
    window.open(course.depoimentu_url);
  };

  return (
    <div className={classes.headerContainer}>
      <Progress />
      <header className={classes.header}>
        <section className={classes.menuGroup}>
          {enableLessonMenu && course && course.customizations?.layout === enLayout.NETFLIX && (
            <Hidden mdUp>
              <IconButton
                id='header-lesson-burger-menu'
                color='inherit'
                aria-label='abrir menu'
                onClick={toggleLessonMenu}
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
          )}

          <Link id='header-showcase-button' to='/vitrine'>
            <div className={classes.hideNutrorIconOnMobile}>
              {!customization.header_logo && (
                <>
                  <SvgIcon name='nutror' />
                  <SvgIcon name='logo' />
                </>
              )}
            </div>
          </Link>
        </section>

        <SearchInputContainer>
          <div className={classes.headerActions}>
            <Hidden xsDown>
              <Box className={classes.headerActions} marginRight={10}>
                {user.email && user.type === 0 && !isSubdomain() && (
                  <Box marginRight={4}>
                    <Typography
                      id='header-turn-producer-button'
                      className={classes.button}
                      onClick={toggleModalTurnProducer}
                    >
                      Criar curso
                    </Typography>
                  </Box>
                )}

                {courseHash && course && course.secretary_status && (
                  <Box marginRight={4}>
                    <Typography id='header-secretary-button' className={classes.button} onClick={hrefToSecretaryPage}>
                      Secretaria
                    </Typography>
                  </Box>
                )}

                {courseHash && course && course.have_active_community && (
                  <Box marginRight={4}>
                    <Typography id='header-community-button' className={classes.button} onClick={hrefToCommunityPage}>
                      Comunidade
                    </Typography>
                  </Box>
                )}

                {courseHash && course && course.depoimentu_url && (
                  <Box marginRight={4}>
                    <Typography id='header-community-button' className={classes.button} onClick={hrefToDepoimentusPage}>
                      Depoimentos
                    </Typography>
                  </Box>
                )}

                {user.email && (
                  <>
                    <Box marginRight={4}>
                      <Typography className={classes.button} onClick={openCertificatesPage}>
                        Meus Certificados
                      </Typography>
                    </Box>
                    <Typography className={classes.button} onClick={openSavedLessonsPage}>
                      Aulas Salvas
                    </Typography>
                  </>
                )}
              </Box>
            </Hidden>
            <SearchIcon />
            {courseHash && isAuthenticated && <News courseHash={courseHash} />}
            {!courseHash && isAuthenticated && <NotificationCenter />}

            {hrefToSupportPage()}

            {isAuthenticated && (
              <Box marginLeft={3}>
                <UserMenu {...props} toggleModalTurnProducer={toggleModalTurnProducer} courseHash={courseHash} />
              </Box>
            )}
          </div>
        </SearchInputContainer>
      </header>
      <TurnProducerDialog isOpen={modalIsOpen} setIsOpen={toggleModalTurnProducer} />
    </div>
  );
};

export default Header;
